import { parseLine } from '@/lib/phone-number';
import type { Line, Sms10DlcStatus } from '@/types/calls-types';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

type LinesContext = {
  teamName: string;
  lines: Line[];
  currentLine: Line;
  setCurrentLine: (line: Line) => void;
};

export const linesContext = createContext<LinesContext>(null as unknown as LinesContext);

type Props = {
  lines: Line[];
  teamName: string;
};

const localStorageKey = 'lines-provider:line';

type StoredLine = {
  lineName: string;
  phoneNumber?: string;
  senderId?: string;
  isShared: boolean;
  isOutboundSMSEnabled: boolean;
  sms10DLCStatus: Sms10DlcStatus;
  isOutboundMMSEnabled: boolean;
  type?: 'REGULAR' | 'SENDER_ID';
  canMakeCalls?: boolean;
  rateGroup?: string;
  status?: 'ACTIVE' | 'UNASSIGNED' | 'PORTING';
};

const findMatchingLine = (storedLine: Line, lines: Line[]): Line | undefined => {
  return lines.find((l) => {
    if (storedLine.senderId && l.senderId) {
      return l.senderId === storedLine.senderId;
    }
    const storedNumber = storedLine.phoneNumber?.number?.format?.('E.164') ?? '';
    const lineNumber = l.phoneNumber?.number?.format?.('E.164') ?? '';
    if (storedNumber && lineNumber) {
      return storedNumber === lineNumber;
    }
    return false;
  });
};

// Mirrors the call/dialer selector filter: only ACTIVE numbers can be used
// for outbound. PORTING/UNASSIGNED lines must not become the default
// currentLine or they get hidden by the selector with no way to switch.
const isCallable = (line: Line) =>
  line.canMakeCalls !== false && (line.status ?? 'ACTIVE') === 'ACTIVE';

const pickInitialLine = (storedLine: Line | null | undefined, lines: Line[]): Line | undefined => {
  if (storedLine) {
    const matched = findMatchingLine(storedLine, lines);
    if (matched && isCallable(matched)) {
      return matched;
    }
  }
  return lines.find(isCallable) ?? lines[0];
};

export const LinesProvider = ({ children, lines, teamName }: React.PropsWithChildren<Props>) => {
  const [line, setLine] = useState<Line>(() => {
    return pickInitialLine(getStoredLine(), lines)!;
  });

  useEffect(() => {
    const picked = pickInitialLine(getStoredLine(), lines);
    if (picked) {
      setLine(picked);
    }
  }, [lines]);

  const setCurrentLine = useCallback((line: Line) => {
    localStorage.setItem(
      localStorageKey,
      JSON.stringify({
        lineName: line.lineName,
        phoneNumber: line.phoneNumber.number?.formatInternational(),
        senderId: line.senderId,
        isShared: line.isShared ?? false,
        isOutboundSMSEnabled: line.isOutboundSMSEnabled ?? false,
        sms10DLCStatus: line.sms10DLCStatus ?? null,
        isOutboundMMSEnabled: line.isOutboundMMSEnabled ?? false,
        type: line.type,
        canMakeCalls: line.canMakeCalls,
        rateGroup: line.rateGroup,
        status: line.status,
      } satisfies StoredLine),
    );

    setLine(line);
  }, []);

  const values = useMemo(
    () => ({ teamName, lines, currentLine: line, setCurrentLine }),
    [teamName, lines, line, setCurrentLine],
  );

  return <linesContext.Provider value={values}>{children}</linesContext.Provider>;
};

export function getStoredLine() {
  const storedLine = localStorage.getItem(localStorageKey);
  const parsedLine = storedLine ? (JSON.parse(storedLine) as Partial<StoredLine>) : null;

  if (!parsedLine) {
    return null;
  }

  // Handle both SENDER_ID and REGULAR lines
  const line = parseLine({
    number: parsedLine.phoneNumber,
    name: parsedLine.lineName,
    isShared: parsedLine.isShared,
    isOutboundSMSEnabled: parsedLine.isOutboundSMSEnabled ?? false,
    sms10DLCStatus: parsedLine.sms10DLCStatus ?? null,
    isOutboundMMSEnabled: parsedLine.isOutboundMMSEnabled ?? false,
    type: parsedLine.type ?? 'REGULAR',
    senderId: parsedLine.senderId,
    canMakeCalls: parsedLine.canMakeCalls,
    rateGroup: parsedLine.rateGroup,
    status: parsedLine.status,
  });

  return line;
}
